// Bulle d'aide « ? » (décision de Maxime du 25/10/2026) : un petit rond à côté d'un libellé, qui explique une notion
// du produit (score, sourcé ou déduit, crédits…). Sur ordinateur elle s'ouvre au survol ; sur téléphone, où le survol
// n'existe pas, au toucher, et se ferme en touchant ailleurs. Les textes sont dans `resources.projects.help`.
import { CircleHelp } from "lucide-react";
import { useTranslate } from "ra-core";
import { useRef, useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const OPEN_DELAY = 150;
const CLOSE_DELAY = 120;

export const HelpTip = ({
  topic,
  options,
  className,
}: {
  /** Clé sous `resources.projects.help` : `<topic>.title` et `<topic>.body`. */
  topic: string;
  options?: object;
  className?: string;
}) => {
  const translate = useTranslate();
  const [open, setOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const later = (next: boolean, delay: number) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setOpen(next), delay);
  };
  // Seule la souris ouvre au survol : un doigt passe par le clic.
  const hover = (next: boolean) => (event: React.PointerEvent) => {
    if (event.pointerType === "mouse")
      later(next, next ? OPEN_DELAY : CLOSE_DELAY);
  };
  // Le survol a déjà ouvert la bulle à la souris : un clic la laisse ouverte au lieu de la refermer.
  const pointer = useRef("");
  const title = translate(`resources.projects.help.${topic}.title`, options);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {/* Un <span> plutôt qu'un bouton : la bulle peut se placer dans un en-tête de colonne triable, déjà un bouton. */}
      <PopoverTrigger
        asChild
        onPointerEnter={hover(true)}
        onPointerLeave={hover(false)}
        onPointerDown={(event) => {
          pointer.current = event.pointerType;
        }}
        // Un clic ne doit pas trier une colonne ni ouvrir la ligne qui porte la bulle.
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          clearTimeout(timer.current);
          setOpen((value) => pointer.current === "mouse" || !value);
        }}
      >
        <span
          role="button"
          tabIndex={0}
          aria-label={title}
          onKeyDown={(event) => {
            if (event.key !== "Enter" && event.key !== " ") return;
            event.preventDefault();
            event.stopPropagation();
            setOpen((value) => !value);
          }}
          className={cn(
            "inline-flex size-6 shrink-0 items-center justify-center rounded-full align-middle text-muted-foreground/70 transition-colors hover:text-foreground focus-visible:text-foreground focus-visible:outline-2 focus-visible:outline-ring data-[state=open]:text-foreground -my-1",
            className,
          )}
        >
          <CircleHelp className="size-3.5" aria-hidden />
        </span>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        collisionPadding={16}
        className="w-auto max-w-[min(18rem,calc(100vw-2rem))] p-3 text-sm font-normal normal-case tracking-normal"
        onPointerEnter={hover(true)}
        onPointerLeave={hover(false)}
        onOpenAutoFocus={(event) => event.preventDefault()}
        onClick={(event) => event.stopPropagation()}
      >
        <p className="font-semibold">{title}</p>
        <p className="mt-1 text-muted-foreground leading-snug">
          {translate(`resources.projects.help.${topic}.body`, options)}
        </p>
      </PopoverContent>
    </Popover>
  );
};
